import { Helmet } from '@dr.pogodin/react-helmet';
import { CaretRightIcon, CheckIcon, XIcon } from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';
import { useLocation, useParams } from 'react-router-dom';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';

import { Api } from '../../api';
import {
  PageError,
  PageLoading,
  PageWrap,
  Pagination,
  RouterLink,
  Stack,
  Text,
} from '../../components';
import { Color, Style } from '../../constants';
import { useCompany } from '../../contexts/ClientDataContext';
import CommitAuthorAvatar from './components/CommitAuthorAvatar';

type Commit = Api.EnvironmentDeploymentCommit.V;

type CommitGroup = {
  date: string;
  commits: Commit[];
};

// Group commits under a per-day header, preserving the API order.
function groupByDate(commits: Commit[]): CommitGroup[] {
  const groups = new Map<string, Commit[]>();
  for (const commit of commits) {
    const date = format(parseISO(commit.timestamp), 'MMM d, yyyy');
    const list = groups.get(date);
    if (list) {
      list.push(commit);
    } else {
      groups.set(date, [commit]);
    }
  }
  return Array.from(groups, ([date, dateCommits]) => ({
    date,
    commits: dateCommits,
  }));
}

export default function CommitHistoryPage() {
  const { companySlug, deploymentId } = useParams<{
    companySlug: string;
    deploymentId: string;
  }>();
  const company = useCompany(companySlug!);
  const { pathname, search } = useLocation();

  const searchParams = new URLSearchParams(search);
  const api = Api.EnvironmentDeploymentCommit.useReadCollectionCommits(
    deploymentId!,
    {
      page: searchParams.has('page')
        ? parseInt(searchParams.get('page') ?? '', 10)
        : 1,
    }
  );

  if (api.error) return <PageError {...api} />;
  if (!api.data) return <PageLoading />;

  const groups = groupByDate(api.data['hydra:member']);

  return (
    <PageWrap background={Color.neutral5} noPadding limitWidth="large">
      <Helmet>
        <title>{`Commit history | ${company.name}`}</title>
      </Helmet>

      <Stack padH={1.5} padV={1.5} gap={1.5}>
        <html.div style={[Style.row, Style.alignCenter, styles.breadcrumb]}>
          <RouterLink to={`/company/${companySlug}/development/environments`}>
            <Text typeface="body.small.regular" color="neutral60">
              Environments
            </Text>
          </RouterLink>
          <CaretRightIcon size={14} color={Color.neutral40} />
          <Text typeface="body.small.regular" color="neutral100">
            Commit history
          </Text>
        </html.div>

        <Text as="h1" typeface="headline" color="neutral100">
          Commit history
        </Text>

        {groups.length === 0 ? (
          <Text typeface="body.regular" color="neutral60">
            No commits yet.
          </Text>
        ) : (
          groups.map((group) => (
            <Stack key={group.date} gap={0.5}>
              <Text typeface="body.small.regular" color="neutral60">
                Commits on {group.date}
              </Text>
              {group.commits.map((commit) => (
                <CommitRow key={commit.hash} commit={commit} />
              ))}
            </Stack>
          ))
        )}

        {/* Only render paging when the backend actually returns it. */}
        {api.data['hydra:view'] && (
          <Pagination
            hydraCollection={api.data}
            pageUrl={(n) => `${pathname}?page=${n}`}
            align="left"
          />
        )}
      </Stack>
    </PageWrap>
  );
}

function CommitRow({ commit }: { commit: Commit }) {
  return (
    <html.div style={[Style.row, Style.alignCenter, styles.row]}>
      <CommitAuthorAvatar author={commit.author} size={24} />
      <Text typeface="body.small.medium" color="neutral100">
        {commit.title}
      </Text>
      <Text typeface="body.small.regular" color="neutral60">
        {format(parseISO(commit.timestamp), 'd MMM yyyy, HH:mm')}
      </Text>
      {commit.checkStatus === 'success' ? (
        <CheckIcon size={16} color={Color.green} />
      ) : commit.checkStatus ? (
        <XIcon size={16} color={Color.accent} />
      ) : null}
    </html.div>
  );
}

const styles = css.create({
  breadcrumb: {
    gap: 6,
  },
  row: {
    gap: 8,
    flexWrap: 'wrap',
    paddingBlock: 12,
    paddingInline: 16,
    backgroundColor: color.neutral10,
    borderRadius: 100,
  },
});
